class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.hoverTimeout = null;
    this.isHovering = false;
    
    // Add hover event listeners
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    
    // Also listen to nested sub-menu hover events
    this.addEventListener('mouseenter', this.onSubMenuMouseEnter.bind(this), true);
    this.addEventListener('mouseleave', this.onSubMenuMouseLeave.bind(this), true);
  }

  onMouseEnter(event) {
    // Clear any pending close timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    
    this.isHovering = true;
    this.open();
  }

  onMouseLeave(event) {
    this.isHovering = false;
    
    // Add a small delay before closing to prevent flickering
    this.hoverTimeout = setTimeout(() => {
      if (!this.isHovering) {
        this.close();
        this.closeAllSubMenus();
      }
    }, 150);
  }

  onSubMenuMouseEnter(event) {
    // Handle nested submenu hover
    const nestedDetails = event.target.closest('details:not([id^="Details-HeaderMenu-"])');
    if (nestedDetails && nestedDetails !== this.mainDetailsToggle) {
      // Clear any timeout for this specific submenu
      if (nestedDetails.closeTimeout) {
        clearTimeout(nestedDetails.closeTimeout);
        nestedDetails.closeTimeout = null;
      }
      
      // Open the submenu
      if (!nestedDetails.hasAttribute('open')) {
        nestedDetails.setAttribute('open', '');
        const summary = nestedDetails.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', 'true');
          summary.classList.add('submenu-active');
        }
      }
    }
  }

  onSubMenuMouseLeave(event) {
    // Handle nested submenu leave
    const nestedDetails = event.target.closest('details:not([id^="Details-HeaderMenu-"])');
    if (nestedDetails && nestedDetails !== this.mainDetailsToggle) {
      // Add delay before closing submenu
      nestedDetails.closeTimeout = setTimeout(() => {
        if (nestedDetails && !nestedDetails.matches(':hover')) {
          nestedDetails.removeAttribute('open');
          const summary = nestedDetails.querySelector('summary');
          if (summary) {
            summary.setAttribute('aria-expanded', 'false');
            summary.classList.remove('submenu-active');
          }
        }
      }, 150);
    }
  }

  open() {
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'true');
        summary.classList.add('menu-active');
      }
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      summary.classList.remove('menu-active');
    }
    this.closeAllSubMenus();
  }

  closeAllSubMenus() {
    // Close all nested submenus
    const subMenus = this.querySelectorAll('details[id^="Details-HeaderSubMenu-"]');
    subMenus.forEach(submenu => {
      if (submenu.closeTimeout) {
        clearTimeout(submenu.closeTimeout);
        submenu.closeTimeout = null;
      }
      submenu.removeAttribute('open');
      const summary = submenu.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
        summary.classList.remove('submenu-active');
      }
    });
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  // Override onFocusOut to work with hover behavior
  onFocusOut() {
    // Only close on focus out if we're not hovering
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.isHovering) {
        this.close();
      }
    });
  }
}

customElements.define('header-menu', HeaderMenu);
